/**
 * S10 — My agent instances (`/ai-studio/instances`), WF-F3.
 * FlexibleCRUD list with on-demand filters and instance lifecycle actions.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PlayIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import {
  InstanceCatalogFilterPanel,
  InstanceCatalogFilterToggle,
  EMPTY_INSTANCE_CATALOG_FILTERS,
  instanceFiltersToQueryParams,
} from '@/components/ai-studio/instances/InstanceCatalogFilters';
import type { InstanceCatalogFilterValues } from '@/components/ai-studio/instances/InstanceCatalogFilters';
import {
  InstanceNotRunnableError,
  InstanceSetupRequiredError,
  resolveInstanceEntry,
} from '@/components/ai-studio/instances/resolveInstanceEntry';
import { isInstanceRunnable } from '@/components/ai-studio/instances/instanceRunEligibility';
import { createAiStudioInstanceEntity } from '@/config/entities/aiStudioInstances';
import { aiStudioInstancesCRUDService } from '@/services/aiStudioInstancesCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioAgentInstance } from '@/types/aiStudioAgent';

function isInstanceReady(instance: AiStudioAgentInstance): boolean {
  return isInstanceRunnable(instance);
}

const AIStudioInstanceCatalogPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deployAgentId = searchParams.get('agentId') ?? undefined;
  const shouldOpenDeploy = searchParams.get('deploy') === '1' || Boolean(deployAgentId);

  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [tableKey, setTableKey] = useState(0);
  const [filters, setFilters] = useState<InstanceCatalogFilterValues>(EMPTY_INSTANCE_CATALOG_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [runningInstanceId, setRunningInstanceId] = useState<string | null>(null);

  const extraQueryParams = useMemo(() => instanceFiltersToQueryParams(filters), [filters]);

  const refreshTable = useCallback(() => {
    setTableKey((key) => key + 1);
  }, []);

  const instanceEntity = useMemo(
    () =>
      createAiStudioInstanceEntity({
        onStatusChange: refreshTable,
        onStatusError: () => setErrorKey('aiStudio.instances.saveFailed'),
      }),
    [refreshTable]
  );

  useEffect(() => {
    if (!shouldOpenDeploy) return;
    const query = deployAgentId ? `?agentId=${encodeURIComponent(deployAgentId)}` : '';
    navigate(`/ai-studio/instances/provision${query}`, { replace: true });
  }, [shouldOpenDeploy, deployAgentId, navigate]);

  const openInstance = useCallback(
    (instance: AiStudioAgentInstance) => {
      navigate(`/ai-studio/instances/${encodeURIComponent(instance.id)}`);
    },
    [navigate]
  );

  const openProvision = useCallback(
    (instance: AiStudioAgentInstance) => {
      navigate(`/ai-studio/instances/${encodeURIComponent(instance.id)}/provision`);
    },
    [navigate]
  );

  const handleRowClick = useCallback(
    (instance: AiStudioAgentInstance) => {
      if (instance.status === 'inactive' || instance.status === 'archived') {
        return;
      }
      if (!isInstanceRunnable(instance)) {
        openProvision(instance);
        return;
      }
      openInstance(instance);
    },
    [openInstance, openProvision]
  );

  const handleRun = useCallback(
    async (instance: AiStudioAgentInstance) => {
      if (runningInstanceId) return;
      setErrorKey(null);
      setRunningInstanceId(instance.id);
      try {
        const { runId, conversationId } = await resolveInstanceEntry(instance.id);
        const params = new URLSearchParams({
          conversationId,
          instanceId: instance.id,
        });
        navigate(`/ai-studio/runs/${encodeURIComponent(runId)}?${params.toString()}`);
      } catch (error: unknown) {
        if (error instanceof InstanceSetupRequiredError) {
          navigate(`/ai-studio/instances/${encodeURIComponent(instance.id)}/provision`);
          return;
        }
        if (error instanceof InstanceNotRunnableError) {
          setErrorKey(
            error.reason === 'inactive'
              ? 'aiStudio.instances.notRunnableInactive'
              : error.reason === 'archived'
                ? 'aiStudio.instances.notRunnableArchived'
                : 'aiStudio.instances.setupRequired'
          );
          return;
        }
        setErrorKey('aiStudio.instances.saveFailed');
      } finally {
        setRunningInstanceId(null);
      }
    },
    [navigate, runningInstanceId]
  );

  const handleDelete = useCallback(async (instance: AiStudioAgentInstance) => {
    setErrorKey(null);
    try {
      await aiStudioInstancesCRUDService.delete(instance.id);
      setTableKey((key) => key + 1);
    } catch {
      setErrorKey('aiStudio.instances.deleteFailed');
    }
  }, []);

  const canEditInstance = useCallback(
    (instance: AiStudioAgentInstance) => instance.status !== 'archived',
    []
  );

  if (shouldOpenDeploy) {
    return (
      <main className="px-4 py-4 md:px-6 lg:px-8">
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.instances.loading')}
        </p>
      </main>
    );
  }

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.instances.title')}
          </h1>
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.instances.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/ai-studio/instances/provision')}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
        >
          {t('aiStudio.instances.deployInstance')}
        </button>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(errorKey)}
        </div>
      )}

      <FlexibleCRUDTable
        key={`${tableKey}-${JSON.stringify(extraQueryParams)}`}
        entity={instanceEntity}
        service={aiStudioInstancesCRUDService}
        showCreateButton={false}
        showSearch
        showPagination
        pageSize={20}
        searchPlaceholder={t('aiStudio.instances.search')}
        useConfirmDialog
        extraQueryParams={extraQueryParams}
        toolbarAfterSearch={
          <InstanceCatalogFilterToggle
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            value={filters}
            onChange={setFilters}
          />
        }
        belowToolbar={
          filtersOpen ? (
            <InstanceCatalogFilterPanel value={filters} onChange={setFilters} />
          ) : null
        }
        onEdit={openProvision}
        editActionLabel={t('common.edit')}
        onDelete={handleDelete}
        canEdit={canEditInstance}
        canDelete={() => true}
        customActions={[
          {
            label: t('aiStudio.instances.runFromInstance'),
            icon: PlayIcon,
            variant: 'primary',
            visible: (instance: AiStudioAgentInstance) => isInstanceReady(instance),
            onClick: (instance: AiStudioAgentInstance) => {
              void handleRun(instance);
            },
          },
          {
            label: t('aiStudio.instances.actionSetup'),
            icon: WrenchScrewdriverIcon,
            variant: 'secondary',
            visible: (instance: AiStudioAgentInstance) =>
              instance.status === 'active' && !isInstanceReady(instance),
            onClick: openProvision,
          },
        ]}
        onRowClick={handleRowClick}
      />
    </main>
  );
};

export default AIStudioInstanceCatalogPage;
